import {
  DraggerUploadFiles,
  type DraggerUploadFilesRef,
  type UploadFileWithId,
} from '@/features/upload-files'
import { type PolicyModel, type UserPolicyTypeEnumEnum } from '@/shared/api'
import { HidingSpin } from '@/shared/ui'
import {
  EllipsisOutlined,
  EyeOutlined,
  RetweetOutlined,
} from '@ant-design/icons'
import { Button, Dropdown, Flex } from 'antd'
import { type MenuProps } from 'antd/lib'
import { useFormik } from 'formik'
import { z } from 'zod'
import { toFormikValidationSchema } from 'zod-formik-adapter'

import { POLICY_LANGUAGE_CODE } from '../../lib/constants'
import { getPolicyRequestByFormValues } from '../../lib/get-policy-request'

interface MenuHandlers {
  viewIsAvailable: boolean
  changeIsAvailable: boolean
  onView: () => void
  onChange: () => void
}

function getMenuItems({
  viewIsAvailable,
  changeIsAvailable,
  onView,
  onChange,
}: MenuHandlers): MenuProps['items'] {
  return [
    {
      key: 'view',
      label: (
        <Flex gap={8}>
          <EyeOutlined />
          Просмотр файла
        </Flex>
      ),
      onClick: onView,
      disabled: !viewIsAvailable,
    },
    {
      key: 'change',
      label: (
        <Flex gap={8}>
          <RetweetOutlined />
          Заменить
        </Flex>
      ),
      onClick: onChange,
      disabled: !changeIsAvailable,
    },
  ]
}

const policySchema = z.object({
  file: z.custom<UploadFileWithId>().nullable(),
  languageCode: z.string(),
  type: z.custom<UserPolicyTypeEnumEnum>(),
})

export type PolicyFormModel = z.infer<typeof policySchema>

interface PolicySectionProps {
  title: string
  data: PolicyModel | null
  pending: boolean
  error: unknown
  type: UserPolicyTypeEnumEnum
  onSubmit: (data: ReturnType<typeof getPolicyRequestByFormValues>) => void
  draggerRef: React.RefObject<DraggerUploadFilesRef | null>
}
export const PolicySection = ({
  title,
  data,
  pending,
  error,
  type,
  onSubmit,
  draggerRef,
}: PolicySectionProps) => {
  const formik = useFormik<PolicyFormModel>({
    enableReinitialize: true,
    initialValues: {
      file: data?.file
        ? {
            id: data.file.id,
            uid: data.file.id,
            name: data.file.name,
            status: 'done',
            url: data.file.fullCdnUrl,
          }
        : null,
      languageCode: POLICY_LANGUAGE_CODE,
      type,
    },
    validationSchema: toFormikValidationSchema(policySchema),
    onSubmit: (values, { setSubmitting }) => {
      const submitted = getPolicyRequestByFormValues(values)
      onSubmit(submitted)
      setSubmitting(false)
    },
  })

  const handleView = () => {
    window.open(formik.values.file?.url, '_blank', 'noopener,noreferrer')
  }

  const handleChange = () => {
    draggerRef.current?.openFileDialog()
  }

  const menuItems = getMenuItems({
    viewIsAvailable: Boolean(formik.values.file?.url),
    changeIsAvailable: Boolean(formik.values.file),
    onView: handleView,
    onChange: handleChange,
  })

  return (
    <section
      style={{
        padding: '24px',
        borderRadius: '8px',
        backgroundColor: '#fff',
      }}
    >
      <HidingSpin spinning={pending || (!data && !error)}>
        <Flex vertical gap={0}>
          <Flex justify="space-between" style={{ placeItems: 'center' }}>
            {title}
            <Dropdown
              menu={{ items: menuItems }}
              trigger={['click']}
              placement="bottom"
            >
              <Button shape="circle" type="text">
                <EllipsisOutlined />
              </Button>
            </Dropdown>
          </Flex>
          <DraggerUploadFiles
            ref={draggerRef}
            maxCount={1}
            accept=".html"
            showRemoveIcon={false}
            value={formik.values.file ? [formik.values.file] : null}
            onChange={(fileList) => {
              const file = fileList[0] || null
              void formik.setFieldValue('file', file, true).then(() => {
                if (Boolean(file) && file.status === 'done') {
                  formik.handleSubmit()
                }
              })
            }}
          />
        </Flex>
      </HidingSpin>
    </section>
  )
}
